import { filter, findIndex, findLastIndex, isString, negate, orderBy } from 'lodash-es';
import React, { HTMLAttributes, MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mountRootParcel } from 'single-spa';

import { usePreferencesItems } from 'hooks';
import icons from 'icons';
import { ListItemIcon, ListItemText, MenuItem } from 'netline-ui';
import services from 'services';
import {
  CloseableComponent,
  NavigationArea,
  PreferencesComponent,
  PreferencesMenuComponentItem,
  PreferencesMenuConfigItem,
  PreferencesMenuDividerItem,
  PreferencesMenuItemType,
  PreferencesMenuWidgetItem,
  PreferencesMenuItem as TPreferencesMenuItem,
} from 'types';
import { logger } from 'utils';

import AboutDialog from '../AppHeader/PreferencesMenu/AboutDialog/AboutDialog';
import CookieSettings from '../AppHeader/PreferencesMenu/CookieSettings';
import DarkModeSelector from '../AppHeader/PreferencesMenu/DarkModeSelector';
import LanguageSelector from '../AppHeader/PreferencesMenu/LanguageSelector';
import TimezoneSelector from '../AppHeader/PreferencesMenu/TimezoneSelector';
import Parcel from '../ApplicationModule/Parcel';
import Divider from '../Divider/Divider';
import MobileAboutDialog from '../MobileNavigation/MobilePreferences/MobileAboutDialog/MobileAboutDialog';
import MobileCookieSettings from '../MobileNavigation/MobilePreferences/MobileCookieSettings/MobileCookieSettings';
import MobileDarkModeSelector from '../MobileNavigation/MobilePreferences/MobileDarkModeSelector/MobileDarkModeSelector';
import MobileLanguageSelector from '../MobileNavigation/MobilePreferences/MobileLanguageSelector/MobileLanguageSelector';
import MobileTimezoneSelector from '../MobileNavigation/MobilePreferences/MobileTimezoneSelector/MobileTimezoneSelector';

const isComponentItem = (item: TPreferencesMenuItem): item is PreferencesMenuComponentItem =>
  item.type === PreferencesMenuItemType.COMPONENT;
const isConfigItem = (item: TPreferencesMenuItem): item is PreferencesMenuConfigItem =>
  item.type === PreferencesMenuItemType.CONFIG;
const isWidgetItem = (item: TPreferencesMenuItem): item is PreferencesMenuWidgetItem =>
  item.type === PreferencesMenuItemType.WIDGET;
const isDividerItem = (item: TPreferencesMenuItem): item is PreferencesMenuDividerItem =>
  item.type === PreferencesMenuItemType.DIVIDER;

const trimAndFilterDuplicatedDividers = (menuItems: TPreferencesMenuItem[]) => {
  const firstItemIdx = findIndex(menuItems, negate(isDividerItem));
  const lastItemIdx = findLastIndex(menuItems, negate(isDividerItem));
  return menuItems
    .slice(Math.max(0, firstItemIdx), lastItemIdx + 1 || menuItems.length)
    .filter((item, idx, trimmed) => idx === 0 || !isDividerItem(item) || !isDividerItem(trimmed[idx - 1]));
};

interface PreferenceMenuItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'onClick'> {
  item: PreferencesMenuConfigItem;
  onClick: (item: PreferencesMenuConfigItem, event: MouseEvent<HTMLLIElement>) => void;
}

const PreferencesMenuItem = ({ item, onClick, ...props }: PreferenceMenuItemProps) => {
  const {
    t,
    i18n: { exists },
  } = useTranslation();
  const { id, icon, text } = item;
  const Icon = (icon && icons[icon]) || undefined;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      onClick?.(item, event);
    },
    [onClick, item],
  );

  return (
    <MenuItem key={id} data-testid={id} onClick={handleClick} {...props}>
      {Icon && (
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
      )}
      <ListItemText>{exists(text) ? t(text) : text}</ListItemText>
    </MenuItem>
  );
};

const sidebarComponentMap: Record<PreferencesComponent, CloseableComponent> = {
  [PreferencesComponent.CookieBanner]: CookieSettings,
  [PreferencesComponent.TimezoneSelector]: TimezoneSelector,
  [PreferencesComponent.DarkModeSelector]: DarkModeSelector,
  [PreferencesComponent.LanguageSelector]: LanguageSelector,
  [PreferencesComponent.AboutDialog]: AboutDialog,
};

const mobileComponentMap: Record<PreferencesComponent, CloseableComponent> = {
  [PreferencesComponent.CookieBanner]: MobileCookieSettings,
  [PreferencesComponent.TimezoneSelector]: MobileTimezoneSelector,
  [PreferencesComponent.DarkModeSelector]: MobileDarkModeSelector,
  [PreferencesComponent.LanguageSelector]: MobileLanguageSelector,
  [PreferencesComponent.AboutDialog]: MobileAboutDialog,
};

const isPreferencesComponent = (
  component: CloseableComponent | PreferencesComponent,
): component is PreferencesComponent => isString(component);

const getPreferencesComponent = (area: NavigationArea, component: CloseableComponent | PreferencesComponent) => {
  return isPreferencesComponent(component)
    ? area === NavigationArea.Sidebar
      ? sidebarComponentMap[component]
      : mobileComponentMap[component]
    : component;
};

const usePreferencesMenuItems = (area: NavigationArea, onMenuClick: () => void, onMenuClose: () => void) => {
  const handleMenuItemClick = useCallback(
    (menuItem: PreferencesMenuConfigItem) => {
      window.postMessage(
        { source: 'preferencesMenu', type: 'menuitemclick', payload: menuItem },
        window.location.origin,
      );
      onMenuClick();
    },
    [onMenuClick],
  );
  const menuItems = usePreferencesItems(area);
  const wrappedItems = useMemo(() => {
    const sortedItems = orderBy(menuItems, ['order'], ['asc']);
    return filter(
      trimAndFilterDuplicatedDividers(sortedItems).map((item, idx, items) => {
        const { id } = item;
        if (isDividerItem(item)) {
          if (idx === 0 || idx === items.length - 1) {
            return null;
          }

          return <Divider key={id} data-preferencesmenuitem data-testid="Preferences-Divider" />;
        }

        const widgetConfig = isWidgetItem(item) && services.getWidget(item.widget);
        if (widgetConfig) {
          return (
            <MenuItem key={id} data-testid={widgetConfig.name}>
              <Parcel config={widgetConfig} services={services} mountParcel={mountRootParcel} />
            </MenuItem>
          );
        }

        if (isConfigItem(item)) {
          return <PreferencesMenuItem key={id} item={item} onClick={handleMenuItemClick} />;
        }

        if (isComponentItem(item)) {
          const { Component: component, ComponentProps } = item;
          const Component = getPreferencesComponent(area, component);
          return <Component key={id} data-testid={id} onMenuClose={onMenuClose} {...ComponentProps} />;
        }

        logger.error('Item is not compatible with preferences menu!', item);
        return null;
      }),
    );
  }, [area, onMenuClose, handleMenuItemClick, menuItems]);

  return wrappedItems;
};

export default usePreferencesMenuItems;
